import { InfoOutlined } from "@mui/icons-material";
import { Stack, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import useStringTranslation from "../../../../../../hooks/useStringTranslation";
import useUser from "../../../../../../hooks/useUser";
import DialogCore from "../../../../components/core/DialogCore";
import { BoxBorder } from "../../../../components/forms/AddBox";
import ActionRow from "../../../../../admin/clinic/components/actions/ActionRow";
import { normalizePetGender } from "../../../../../pets/utils/Functions";

const InfoItem = ({ label, value, testId }) => (
  <Typography data-testid={testId}>
    <strong>{label}:</strong> {value || "—"}
  </Typography>
);

// Actions-dialog row that opens the pet details dialog
export default function PetDetails({ pet }) {
  const { t } = useTranslation();
  const { roles } = useUser();
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <ActionRow
        icon={<InfoOutlined />}
        label={t("ShowDetails")}
        description={t("ShowPetDetailsDescription")}
        onClick={() => setOpen(true)}
        testId="pet.header.actions.details"
      />
      <DialogCore
        open={open}
        onClose={() => setOpen(false)}
        showSubmit={false}
        testId="pet.details-dialog"
        fontWeight={800}
        maxWidth="lg"
        title={
          pet == null
            ? t("PetDetailsTitle")
            : `${pet.name} (${t("PetDetailsTitle")})`
        }
      >
        {pet == null ? (
          <Typography color={"GrayText"}>{t("PetNotFound")}</Typography>
        ) : (
          <Stack spacing={3}>
            <BasicInformation pet={pet} />
            <Identification pet={pet} />
            <BloodDonation pet={pet} />
            {/* The owner already knows who owns the pet, and aggression is clinic-facing */}
            {!roles.user && <Aggression pet={pet} />}
            {!roles.user && <Ownership pet={pet} />}
          </Stack>
        )}
      </DialogCore>
    </>
  );
}

function Identification({ pet = {} }) {
  const { t } = useTranslation();
  if (!pet?.microchip && !pet?.passport) {
    return (
      <Section title={t("Identification")}>
        <Typography color={"GrayText"} data-testid="pet.details.no-identification">
          {t("NoIdentificationData")}
        </Typography>
      </Section>
    );
  }
  return (
    <Section title={t("Identification")}>
      <InfoItem label={t("Microchip")} value={pet?.microchip} testId="pet.details.microchip" />
      <InfoItem label={t("PassportID")} value={pet?.passport} testId="pet.details.passport" />
    </Section>
  );
}

function BloodDonation({ pet = {} }) {
  const { t } = useTranslation();
  return (
    <Section title={t("BloodDonor")}>
      <InfoItem label={t("BloodDonor")} value={pet?.bloodDonor ? t("Yes") : t("No")} testId="pet.details.blood-donor" />
      {pet?.bloodDonor && (
        <InfoItem label={t("BloodType")} value={pet?.bloodType} testId="pet.details.blood-type" />
      )}
    </Section>
  );
}

function Aggression({ pet = {} }) {
  const { t } = useTranslation();
  if (!pet?.aggression) return null;
  return (
    <Section title={t("Aggression")}>
      <Typography sx={{ whiteSpace: "pre-wrap" }} data-testid="pet.details.aggression">
        {pet.aggression}
      </Typography>
    </Section>
  );
}

function BasicInformation({ pet = {} }) {
  const { t } = useTranslation();
  const species = useStringTranslation(pet?.type, "title");

  return (
    <Section title={t("BasicInformation")}>
      <InfoItem label={t("Species")} value={species} testId="pet.details.type" />
      <InfoItem label={t("Breed")} value={t(pet?.breed)} testId="pet.details.breed" />
      <InfoItem
        label={t("BirthDate")}
        value={pet?.birthdate || ""}
        testId="pet.details.dob"
      />
      <InfoItem label={t("Gender")} value={t(normalizePetGender(pet?.gender))} testId="pet.details.gender" />
    </Section>
  );
}

function Ownership({ pet = {} }) {
  const { t } = useTranslation();
  if (pet?.userId) {
    return (
      <Section title={t("Owner")}>
        <InfoItem label={t("Name")} value={pet?.ownerName} testId="pet.details.owner-name" />
        <InfoItem label={t("Email")} value={pet?.ownerEmail} testId="pet.details.owner-email" />
        <InfoItem
          label={t("PhoneNumber")}
          value={pet?.ownerPhoneNumber}
          testId="pet.details.owner-phone"
        />
      </Section>
    );
  }
  if (pet?.clinicId) {
    return (
      <Section title={t("Clinic")}>
        <InfoItem label={t("Name")} value={pet?.clinicShortName} testId="pet.details.clinic-name" />
        <InfoItem label={t("Email")} value={pet?.clinicEmail} testId="pet.details.clinic-email" />
        <InfoItem label={t("PhoneNumber")} value={pet?.clinicPhoneNumber} testId="pet.details.clinic-phone" />
      </Section>
    );
  }
  return (
    <Section title={t("Owner")}>
      <Typography color={"GrayText"}>{t("NoOwnershipData")}</Typography>
    </Section>
  );
}

const Section = ({ title, children }) => (
  <BoxBorder>
    <Typography variant="h6" fontWeight={"bold"} gutterBottom>
      {title}
    </Typography>
    <Stack spacing={1}>{children}</Stack>
  </BoxBorder>
);
